import '../pages/sitStyling.scss';
import '../pages/sitDesign.css';
import React, {useEffect, useRef, useState} from "react";
import {observer} from "mobx-react-lite";
import {MainComponent} from "../../components/main/MainComponent";
import {Tab} from "@mui/material";
import {CustomTabs} from "~/components/tabs/Tabs";
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import {SitLanguageSwitch} from "../components/SitLanguageSwitch";
import {generatePath, useLocation, useNavigate} from "react-router";
import {sitRoutes} from "./sitRoutes";
import {useSitStores} from "../../stores";
import {CurrencyComponent} from "../../components/currency-component/CurrencyComponent";
import {MainPageFacade} from "../../components/main/page/MainPageFacade.tsx";
import {CARD_CONFIG} from "../../components/dashboard/DashboardCardConfigs.tsx";
import type {SitKpiPill} from "../components/SitKpiHeader/SitKpiHeader";
import {SitKpiHeader} from "../components/SitKpiHeader/SitKpiHeader";
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FilterListIcon from '@mui/icons-material/FilterList';
import i18n from "../../i18n/i18n";

export type SitRouteWrapperProps = {
    children?: React.ReactNode
}

export const SitRouteWrapper: React.FC<SitRouteWrapperProps> = observer(({children}) => {
    const {ccStore, initiativeListStore, fbStore} = useSitStores();
    const navigate = useNavigate();
    const location = useLocation();
    const tabLocations = [
        sitRoutes.sit_initiatives,
        sitRoutes.sit_targets,
        sitRoutes.sit_financial,
        sitRoutes.sit_indicators,
        sitRoutes.sit_pipeline_dashboard,
        sitRoutes.sit_approval,
    ];
    const [gearOpen, setGearOpen] = useState(false);
    const gearRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!gearOpen) return;
        const onDown = (e: MouseEvent) => {
            if (gearRef.current && !gearRef.current.contains(e.target as Node)) setGearOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [gearOpen]);

    useEffect(() => {
        ccStore.loadInitialData();
        initiativeListStore.reloadData(true);
        // fbStore.reloadData also fetches the portfolio NExO indicators (same source
        // as the Indicators dashboard) that back the mapped / unlocked / captured pills.
        fbStore.reloadData(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // TODO: Experiment with the correct deduction of the Role before rending any View (on staging)

    const kpi = initiativeListStore.initiativeKpiRM.result;
    const indicators = fbStore.indicators;
    const tk = (k: string) => i18n.t(`topKpi.${k}`, {ns: 'sit'});
    const kpiValues = React.useMemo<SitKpiPill[]>(() => {
        // Key metrics first: SitKpiHeader shows the first four (two on phones), the rest under "All metrics".
        const items: SitKpiPill[] = [
            {
                tone: 'ok', icon: <SavingsOutlinedIcon/>, label: tk('totalSavings'),
                value: <CurrencyComponent v={kpi?.total_savings ?? 0} nDigits={3}/>
            },
            {tone: 'warn', icon: <ScheduleIcon/>, label: tk('pending'), value: kpi?.pending ?? 0},
            // NExO indicators (actuals from getNexoIndicators) — mapped / unlocked / captured.
            {
                tone: 'plain', icon: <LockOpenIcon/>, label: tk('unlocked'),
                value: <CurrencyComponent v={indicators?.unlocked ?? 0} nDigits={3}/>
            },
            {
                tone: 'plain', icon: <CheckCircleOutlineIcon/>, label: tk('captured'),
                value: <CurrencyComponent v={indicators?.captured ?? 0} nDigits={3}/>
            },
            {tone: 'info', icon: <OutlinedFlagIcon/>, label: tk('initiatives'), value: kpi?.count ?? 0},
            {
                tone: 'plain', icon: <TrackChangesIcon/>, label: tk('mapped'),
                value: <CurrencyComponent v={indicators?.mapped ?? 0} nDigits={3}/>
            },
            {
                tone: 'plain', icon: <FilterListIcon/>, label: tk('prioritizedSpend'),
                value: <CurrencyComponent v={indicators?.prioritized_spend ?? 0} nDigits={3}/>
            },
        ];
        return items;
    }, [kpi, indicators]);


    return (
        <MainComponent>
            <div className="sit-design">
                {tabLocations.indexOf(location.pathname) >= 0 &&
                    <MainPageFacade
                        title={CARD_CONFIG['sit'].title}
                        actionBarEnd={
                            <div className="sit-pagehead-tools">
                                <SitLanguageSwitch/>
                                <div className="sit-gearwrap" ref={gearRef}>
                                    <button type="button" className={'ac-icon-btn' + (gearOpen ? ' is-on' : '')}
                                            title={i18n.t('config.title', {ns: 'sit'})}
                                            onClick={() => setGearOpen(o => !o)}>
                                        <SettingsOutlinedIcon/>
                                    </button>
                                    {gearOpen && <div className="sit-gearmenu">
                                        <button type="button" className="sit-gearitem"
                                                onClick={() => {
                                                    setGearOpen(false);
                                                    navigate(generatePath(sitRoutes.sit_settings));
                                                }}>
                                            <SettingsOutlinedIcon
                                                fontSize="small"/> {i18n.t('config.title', {ns: 'sit'})}
                                        </button>
                                    </div>}
                                </div>
                            </div>
                        }
                        className="sit-page">
                        <SitKpiHeader items={kpiValues} primary={4}/>

                        <CustomTabs
                            component="section"
                            className="sit-tabs"
                            value={location.pathname}
                            aria-label="wrapped label tabs"
                            variant="scrollable"
                            scrollButtons="auto"
                            TabIndicatorProps={{style: {display: 'none'}}}
                            onChange={(event, newValue) => {
                                navigate(generatePath(newValue));
                            }}>
                            <Tab label={i18n.t('tab.targets', {ns: 'sit'})} value={sitRoutes.sit_targets}/>
                            <Tab label={i18n.t('tab.initiatives', {ns: 'sit'})} value={sitRoutes.sit_initiatives}/>
                            <Tab label={i18n.t('tab.approval', {ns: 'sit'})} value={sitRoutes.sit_approval}/>
                            <Tab label={i18n.t('tab.pipeline', {ns: 'sit'})} value={sitRoutes.sit_pipeline_dashboard}/>
                            <Tab label={i18n.t('tab.financial', {ns: 'sit'})} value={sitRoutes.sit_financial}/>
                            <Tab label={i18n.t('dash.title', {ns: 'sit'})} value={sitRoutes.sit_indicators}/>
                        </CustomTabs>
                        {children}
                    </MainPageFacade>}
                {tabLocations.indexOf(location.pathname) < 0 && children}
            </div>
        </MainComponent>
    );
});
